import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  isPlatformServer,
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgIf,
  NgStyle,
} from '@angular/common';
import { finalize, Observable, Subscription } from 'rxjs';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { ApiService } from '../../../services/api.service';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { ModelCardComponent } from '../card/model-card/model-card.component';
import { CardChipsComponent } from '../card/card-chips/card-chips.component';
import { CardSkeletonLoaderComponent } from '../../utility/card-skeleton-loader/card-skeleton-loader.component';
import { routes } from '../../../constants/routes';
import { CategoryTypes } from '../../../models/category-types.model';
import { CardType } from '../../../models/card-type.model';
import { Model } from '../../../models/model.model';
import { CouldntLoadDataComponent } from '../couldnt-load-data/couldnt-load-data.component';
import { NoRecordFoundComponent } from '../no-record-found/no-record-found.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { TimeRange } from '../../../models/filter.model';
import moment from 'moment';
import { HttpParams } from '@angular/common/http';
import { cardGradients } from '../../../constants/card-gradients';

@Component({
  selector: 'app-home-model-tryout',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    ModelCardComponent,
    CardChipsComponent,
    NgClass,
    CardSkeletonLoaderComponent,
    CouldntLoadDataComponent,
    NoRecordFoundComponent,
    NgStyle,
  ],
  templateUrl: './home-model-tryout.component.html',
  styleUrl: './home-model-tryout.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeModelTryoutComponent
  implements OnInit, OnDestroy, ApiErrorHandler
{
  selectedCategory!: CategoryTypes;
  isModelsLoading!: boolean;
  private isSwiperInitialize = false;
  models?: Model[];
  readonly CategoryTypes = CategoryTypes;
  readonly TimeRange = TimeRange;
  readonly CardType = CardType;
  readonly routes = routes;
  private fetchModelsSubs!: Subscription;
  public isErrorFetchingModels: boolean = false;
  description = 'Run a model in your browser.';
  selectedTimeRange: TimeRange = TimeRange.week;

  filterParams = new HttpParams();
  cardGradients = cardGradients;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    public toaster: ToasterService,
    private apiService: ApiService,
  ) {}

  ngOnInit() {
    this.selectedCategory = this.CategoryTypes.trending;
    this.selectedTimeRange = this.TimeRange.week;
    this.fetchModels(this.selectedCategory);
  }

  public fetchModels(filterType: string) {
    this.models = [];
    this.isModelsLoading = true;
    let fetchSelectedCategoryModel$: Observable<any>;
    if (filterType === this.CategoryTypes.new) {
      this.filterParams = this.filterParams.set('tryoutType', 'Tryout');
      fetchSelectedCategoryModel$ = this.apiService.fetchModelsForFilters(
        1,
        this.filterParams,
      );
    } else {
      fetchSelectedCategoryModel$ = this.apiService.fetchFilteredModelTryouts(
        this.selectedCategory,
        this.filterParams,
      );
    }

    this.fetchModelsSubs = fetchSelectedCategoryModel$
      .pipe(
        finalize(() => {
          this.isModelsLoading = false;
        }),
      )
      .subscribe({
        next: (resp) => {
          if (filterType === this.CategoryTypes.new) {
            this.models = resp?.data?.cards?.data ?? [];
          } else {
            this.models = resp?.data ?? [];
          }

          if (
            this.models?.length &&
            !this.isSwiperInitialize &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            const swiperContainer: SwiperContainer =
              this.document.querySelector('#model-tryout-swiper-container')!;
            swiperContainer?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingModels = true;
          this.models = [];
          apiErrorHandler.call(this, error);
        },
      });
  }

  private initSwiperCarousel() {
    this.isSwiperInitialize = true;
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,
      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.hero-carousel-arrow--prev',
        nextEl: '.hero-carousel-arrow--next',
      },
      pagination: {
        el: '.model-tryout-swiper-pagination ',
        clickable: true,
      },
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
        },
      },
    };

    const swiperContainer: SwiperContainer = this.document.querySelector(
      '#model-tryout-swiper-container',
    )!;
    Object.assign(swiperContainer, swiperProps);
    swiperContainer.initialize();
  }

  ngOnDestroy() {
    this.fetchModelsSubs.unsubscribe();
  }
}
